import React, { useMemo, useState } from 'react';

// Generic sortable / filterable table.
//
//   columns: [{ key, label, render?(row), sortValue?(row), align?, width? }]
//   rows:    array of objects
//   rowKey:  (row) => string
//   filterKeys: string[] — enables the text filter over those row fields
//   defaultSort: { key, dir: 'asc'|'desc' }
export default function DataTable({
  columns = [],
  rows = [],
  rowKey = (r) => r.id,
  filterKeys = null,
  filterPlaceholder = 'Filter…',
  defaultSort = null,
  limit = 12,
  onRowClick = null,
  empty = 'Nothing here yet.',
}) {
  const [sort, setSort] = useState(defaultSort);
  const [q, setQ] = useState('');
  const [shown, setShown] = useState(limit);

  const filtered = useMemo(() => {
    if (!q.trim() || !filterKeys) return rows;
    const needle = q.trim().toLowerCase();
    return rows.filter((r) =>
      filterKeys.some((k) => String(r[k] ?? '').toLowerCase().includes(needle)));
  }, [rows, q, filterKeys]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const col = columns.find((c) => c.key === sort.key);
    const val = col?.sortValue || ((r) => r[sort.key]);
    return [...filtered].sort((a, b) => {
      const av = val(a);
      const bv = val(b);
      const cmp = typeof av === 'number' && typeof bv === 'number'
        ? av - bv
        : String(av ?? '').localeCompare(String(bv ?? ''));
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sort, columns]);

  const toggleSort = (key) => setSort((s) => (
    s && s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }
  ));

  return (
    <div className="ck-table-wrap">
      {filterKeys && rows.length > 4 && (
        <input
          className="cs-input ck-table-filter"
          value={q}
          onChange={(e) => { setQ(e.target.value); setShown(limit); }}
          placeholder={filterPlaceholder}
        />
      )}
      <table className="ck-table">
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                style={{ textAlign: c.align || 'left', width: c.width }}
                onClick={() => toggleSort(c.key)}
                className={sort?.key === c.key ? 'sorted' : ''}
              >
                {c.label}
                {sort?.key === c.key && <span className="ck-sort">{sort.dir === 'asc' ? ' ▲' : ' ▼'}</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.slice(0, shown).map((r) => (
            <tr
              key={rowKey(r)}
              onClick={onRowClick ? () => onRowClick(r) : undefined}
              className={onRowClick ? 'clickable' : ''}
            >
              {columns.map((c) => (
                <td key={c.key} style={{ textAlign: c.align || 'left' }}>
                  {c.render ? c.render(r) : r[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {!sorted.length && <div className="ck-empty">{empty}</div>}
      {sorted.length > shown && (
        <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost ck-table-more" onClick={() => setShown((n) => n + limit)}>
          Show more ({sorted.length - shown})
        </button>
      )}
    </div>
  );
}
